import React, { useCallback, useRef, useEffect, useState } from 'react';
import {
  Box,
  Stack,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import SquareIcon from '@mui/icons-material/Square';
import CircleIcon from '@mui/icons-material/Circle';

const chanceStatus = [
  { id: 0, value: '', label: '전체', color: 'warning' },
  { id: 1, value: '1', label: '기회확인', color: 'info' },
  { id: 2, value: '2', label: '기회분석', color: 'error' },
  { id: 3, value: '3', label: '매출견적', color: 'success' },
  { id: 4, value: '4', label: '수주품의 ', color: 'secondary' },
];

const projectStatus = [
  { id: 0, value: '', label: '전체', color: 'warning' },
  { id: 1, value: '0', label: '수주품의', color: 'secondary' },
  { id: 2, value: '1', label: '발주', color: 'success' },
  { id: 3, value: '2', label: '세금계산서', color: 'error' },
  { id: 4, value: '3', label: '채권', color: 'primary' },
];

const prjTyps = [
  { id: 0, value: '', label: '전체', color: 'warning' },
  { id: 1, value: 'N', label: '프로젝트', color: 'info' },
  { id: 2, value: 'M', label: '유지보수', color: 'success' },
  { id: 3, value: 'ETC', label: '기타매출', color: 'secondary' },
];

// 개별 텍스트 탭 컴포넌트
const TabItem = ({ label, iconColor, isSelected, onClick, shape, ...props }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      {...props}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        height: '36px',
        paddingBottom: '8px',
        paddingTop: '4px',
        px: '10px',
        userSelect: 'none',
        color: isSelected ? theme.palette.text.primary : theme.palette.text.disabled,
        fontWeight: isSelected ? 700 : 400,
        transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          color: theme.palette.text.primary,
          '& .MuiSvgIcon-root': { opacity: 1 }
        },
        ...props.sx
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {shape === 'square' ? (
          <SquareIcon 
            sx={{ 
              fontSize: '9px',
              borderRadius: '1px',
              color: iconColor,
              opacity: isSelected ? 1 : 0.5,
              margin: 0,
              transition: 'opacity 0.3s ease',
            }} 
          />
        ) : (
          <CircleIcon 
            sx={{ 
              fontSize: '11px',
              color: iconColor,
              opacity: isSelected ? 1 : 0.5,
              margin: 0,
              transition: 'opacity 0.3s ease',
            }} 
          />
        )}
        <Box sx={{ fontSize: '0.875rem', paddingLeft: '7px', whiteSpace: 'nowrap' }}>{label}</Box>
      </Box>
    </Box>
  );
};

// 각 그룹이 독립된 하단 베이스 라인과 인디케이터를 가지도록 캡슐화
const AnimatedTabGroup = ({ items, selectedValue, onSelect, shape }) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [lineStyle, setLineStyle] = useState({ width: 0, transform: 'translateX(0px)', color: 'transparent' });

  useEffect(() => {
    const activeIndex = items.findIndex(item => item.value === selectedValue);
    const container = containerRef.current;
    
    if (container && activeIndex !== -1) {
      const activeTab = container.children[activeIndex];
      if (activeTab) {
        const currentItem = items[activeIndex];
        const mainColor = theme.palette[currentItem.color]?.main || theme.palette.primary.main;
        
        setLineStyle({
          width: `${activeTab.offsetWidth}px`,
          transform: `translateX(${activeTab.offsetLeft}px)`,
          color: mainColor,
        });
      }
    }
  }, [selectedValue, items, theme]);

  return (
    <Box 
      ref={containerRef} 
      sx={{ 
        position: 'relative', 
        display: 'flex', 
        gap: 0,
        borderBottom: `2px solid ${theme.palette.divider}`, 
      }}
    >
      {items.map((st) => (
        <TabItem
          key={st.id}
          label={st.label}
          iconColor={theme.palette[st.color]?.main}
          isSelected={st.value === selectedValue}
          onClick={() => onSelect(st.value)}
          shape={shape}
        />
      ))}
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '-2px',
          left: 0,
          height: '2px',
          width: lineStyle.width,
          transform: lineStyle.transform,
          backgroundColor: lineStyle.color,
          zIndex: 1,
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
        }}
      />
    </Box>
  );
};

const StatusList = ({ filters, onFilters, menu }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const satusList = menu === 'project' ? projectStatus : chanceStatus;

  const getSelectedItem = (list, value) => list.find((item) => item.value === value) || list;

  const handleFilterType = useCallback((val) => {
    onFilters('searchPrjTyp', val);
  }, [onFilters]);

  const handleFilterStatus = useCallback((val) => {
    onFilters('searchGrade', val);
  }, [onFilters]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end', // [변경] 전체 정렬 기준을 좌측에서 다시 우측(flex-end)으로 복원
        alignItems: 'center',
        width: '100%',
      }}
    >
      {isMobile ? (
        /* --- 모바일: 드롭다운 --- */
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          {menu === 'chance' && (
            <FormControl fullWidth size="small">
              <Select
                value={filters.searchPrjTyp || ''}
                onChange={(e) => handleFilterType(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  const item = getSelectedItem(prjTyps, selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
                      <SquareIcon sx={{ fontSize: 11, color: `${item.color}.main` }} />
                      {item.label}
                    </Box>
                  );
                }}
              >
                {prjTyps.map((st) => (
                  <MenuItem key={st.id} value={st.value} sx={{ fontSize: '0.875rem' }}>
                    <SquareIcon sx={{ fontSize: 11, mr: 1, color: `${st.color}.main` }} />
                    {st.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth size="small">
            <Select
              value={filters.searchGrade || ''}
              onChange={(e) => handleFilterStatus(e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                const item = getSelectedItem(satusList, selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
                    <CircleIcon sx={{ fontSize: 11, color: `${item.color}.main` }} />
                    {item.label}
                  </Box>
                );
              }}
            >
              {satusList.map((st) => (
                <MenuItem key={st.id} value={st.value} sx={{ fontSize: '0.875rem' }}>
                  <CircleIcon sx={{ fontSize: 11, mr: 1, color: `${st.color}.main` }} />
                  {st.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      ) : (
        /* --- 데스크탑: 다시 우측으로 나란히 붙도록 정렬 복원 완료 --- */
        <Stack
          direction="row"
          spacing={2.5}
          sx={{ alignItems: 'center' }}
        >
          {menu === 'chance' && (
            <AnimatedTabGroup 
              items={prjTyps} 
              selectedValue={filters.searchPrjTyp || ''} 
              onSelect={handleFilterType} 
              shape="square"
            />
          )}
          <AnimatedTabGroup 
            items={satusList} 
            selectedValue={filters.searchGrade || ''} 
            onSelect={handleFilterStatus} 
            shape="circle"
          />
        </Stack>
      )}
    </Box>
  );
};

export default StatusList;
